// @ts-nocheck

import React, { memo, useState, useRef, useEffect } from 'react';
import { Handle, Position, useViewport } from '@xyflow/react';
import { cn } from '@/lib/utils';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Pass2Option } from '../../../typesV2';
import { EntitreeNode } from '../../../typesV2';
import { TooltipContent } from '../components/TooltipContent';
import { useParams } from 'next/navigation';
import { slugify } from '../../../lib/map/slugify';
import Link from 'next/link';
import { useAtom, useAtomValue } from 'jotai';
import {
  contentAtom,
  isLoadingNavAtom,
  isStreamingMapAtom,
  mobileSidebar,
} from '../../../jotaiStore';
import {
  showNavigationWarningToast,
  dismissNavigationWarningToast,
} from '@/lib/toast';
import LoadingSpinnerSmall from '../../../features/global/loading/LoadingSpinnerSmall';
import getContentLink from '@/lib/map/getContentLink';
const { Top, Bottom, Left, Right } = Position;

const colorMap = {
  red: '#EA9F9F',
  // peach: '#EAA99F',
  // orange: '#EAB29F',
  yellow: '#EAC99F',
  teal: '#A4EAC0',
  sky: '#A3E1EB',
  lavender: '#B6BAE2',
};

const availableColors = Object.keys(colorMap);

// Keep track of last used colors across all options
const lastUsedColors = new Set<string>();
const MEMORY_SIZE = 2; // How many previous colors to remember

const getRandomColor = () => {
  // Filter out recently used colors
  const validColors = availableColors.filter(
    (color) => !lastUsedColors.has(color)
  );

  // If we've used all colors, reset and just avoid the most recent color
  if (validColors.length === 0) {
    const lastColor = Array.from(lastUsedColors)[lastUsedColors.size - 1];
    return availableColors.filter((color) => color !== lastColor)[0];
  }

  const randomIndex = Math.floor(Math.random() * validColors.length);
  const colorName = validColors[randomIndex];

  // Update last used colors
  lastUsedColors.add(colorName);
  if (lastUsedColors.size > MEMORY_SIZE) {
    const firstUsed = Array.from(lastUsedColors)[0];
    lastUsedColors.delete(firstUsed);
  }

  return colorMap[colorName];
};

interface OptionProps {
  option: Pass2Option;
  index: number;
  mapSlug: string;
  totalOptions: number;
}

const OptionLink = ({ option, index, mapSlug, totalOptions }: OptionProps) => {
  const [randomColor] = useState(() => getRandomColor());
  const [, setIsLoadingNav] = useAtom(isLoadingNavAtom);
  const [, setContentData] = useAtom(contentAtom);
  const isStreamingMap = useAtomValue(isStreamingMapAtom);
  const { x, y, zoom } = useViewport();
  const [showMobileSidebar, setShowMobileSidebar] = useAtom(mobileSidebar);
  const { contentSlug } = useParams();
  const contentLink = getContentLink(
    x,
    y,
    zoom,
    contentSlug as string,
    mapSlug,
    option.optionTitle
  );

  useEffect(() => {
    if (!isStreamingMap) {
      dismissNavigationWarningToast();
    }
  }, [isStreamingMap]);

  return (
    <Tooltip.Root delayDuration={100} className="pointer-events-auto">
      <Link
        href={contentLink}
        className="w-full"
        onClick={(e) => {
          if (isStreamingMap) {
            e.preventDefault();
            showNavigationWarningToast();
          } else {
            if (showMobileSidebar) setShowMobileSidebar(false);
            setIsLoadingNav(true);
            setContentData('');
          }
        }}
      >
        <Tooltip.Trigger
          asChild
          id={`option-${index}-tooltip`}
          className="pointer-events-auto"
        >
          <div className="flex flex-col h-full p-6 py-6 transition-all duration-200 rounded-lg cursor-pointer bg-shades-baseLightest hover:bg-shades-baseLighterist">
            <div className="flex items-start">
              <span
                style={{ backgroundColor: randomColor }}
                className="text-[20px] tracking-wider leading-6 font-medium rounded-full px-4 py-1"
              >
                {option.optionTag?.toUpperCase()}
              </span>
            </div>
            <h3 className="text-shades-textLight text-[26px] underline font-medium mt-1">
              {option.optionTitle}
            </h3>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <TooltipContent
            title={option.optionTitle}
            description={option.optionDescription}
            mapSlug={mapSlug}
            labelText={option.optionTag?.toUpperCase() || ''}
            labelStyle={randomColor}
            side="right"
          />
        </Tooltip.Portal>
      </Link>
    </Tooltip.Root>
  );
};

const PluralisticNode = memo((node: EntitreeNode) => {
  const params = useParams();
  const mapSlug = Array.isArray(params.mapSlug)
    ? params.mapSlug[0]
    : params.mapSlug || '';

  const { isSpouse, isSibling, data } = node;

  // const getTargetPosition = () => {
  //   if (isSpouse) {
  //     return Left;
  //   } else if (isSibling) {
  //     return Right;
  //   }
  //   return Top;
  // };

  const hasChildren = !!node?.children?.length;
  const hasSiblings = !!node?.siblings?.length;
  const hasSpouses = !!node?.spouses?.length;
  const hasSubtopics = !!data?.sections?.length;
  const isSideQuest = !!node?.isSibling || !!node?.isSpouse;

  return (
    <div>
      <div
        className={cn(
          'nodrag border-[4px] rounded-lg border-colors-green pointer-events-none cursor-default'
        )}
      >
        {/* For children */}
        {/* {hasChildren && <Handle type="source" position={Bottom} id={Bottom} />} */}

        {/* For spouses */}
        {hasSpouses && <Handle type="source" position={Right} id={Right} />}

        {/* For siblings */}
        {hasSiblings && <Handle type="source" position={Left} id={Left} />}

        {/* Source Handle */}
        <Handle type={'source'} position={Bottom} id={Bottom} />

        {/* Target Handle */}
        <Handle type={'target'} position={Top} id={Top} />

        <div className="max-w-3xl mx-auto cursor-default">
          <div className="overflow-hidden border-shades-surfaceLight cursor-default">
            <div className="p-6 bg-shades-base cursor-default">
              <div className="flex items-center gap-4 mb-4 cursor-default">
                <div className="flex items-center justify-center w-12 h-12 text-3xl font-bold rounded-lg bg-colors-green text-shades-baseDark cursor-default">
                  {data.coreTopicNumber}
                </div>
                <h2 className="text-3xl font-bold text-shades-textLight cursor-default">
                  {data.topicTitle}
                </h2>
              </div>

              <p className="mb-6 text-2xl text-shades-text">
                {data.topicDescription}
              </p>

              <div className="mb-3">
                <h3 className="text-[22px] font-semibold tracking-wider uppercase text-shades-text opacity-90">
                  OPTIONS (
                  <span className="text-[24px]">
                    {data.options?.length || 0}
                  </span>
                  )
                </h3>
              </div>

              <div className="flex flex-col gap-4">
                {data.options?.map((option, index) => (
                  <OptionLink
                    key={index}
                    option={option}
                    index={index}
                    mapSlug={mapSlug}
                    totalOptions={data.options?.length || 0}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

PluralisticNode.displayName = 'PluralisticNode';

export default PluralisticNode;
